/** Area types offered at creation, as the mock lists them. */
export const AREA_TYPES = [
  'Areas of Interest',
  'Regulatory Areas',
  'Geology',
  'Units',
  'Tracts',
  'Type Curve Area',
] as const;

export type AreaType = (typeof AREA_TYPES)[number];

/**
 * The `category` each label persists as. `area_geometries.category` is free
 * text, so the first three reuse the values already in the table; Units, Tracts
 * and Type Curve Area have no rows yet and take these new values.
 */
export const CATEGORY_BY_AREA_TYPE: Record<AreaType, string> = {
  'Areas of Interest': 'AOI',
  'Regulatory Areas': 'Regulatory',
  'Geology': 'GEOLOGY',
  'Units': 'Unit',
  'Tracts': 'Tract',
  'Type Curve Area': 'TypeCurve',
};

/**
 * Creation methods. `sourceMethod` is what the backend's create endpoint takes;
 * the mock's own ids differ and aren't used here.
 */
export const CREATION_METHODS = [
  {
    id: 'selected',
    title: 'New area from selected',
    description: 'Composite area from selection',
  },
  {
    id: 'drawn',
    title: 'User-drawn on map',
    description: 'Draft or active custom area',
  },
  {
    id: 'survey',
    title: 'Area from Survey',
    description: 'Survey-backed unit, tract, or AOI',
  },
  {
    id: 'upload',
    title: 'Upload shape file',
    description: 'Shapefile (.zip) or GeoJSON',
  },
] as const;

export type CreationMethod = (typeof CREATION_METHODS)[number]['id'];

/** The shapes the drawn method offers (Epic 2), as the mock lists them. */
export const DRAW_SHAPES = [
  {
    id: 'polygon',
    title: 'Polygon',
    description: 'Click vertices to trace the boundary',
    hint: 'Click the map to add vertices. Double-click or click the first vertex to finish; Backspace removes the last vertex, Esc cancels.',
  },
  {
    id: 'circle',
    title: 'Circle radius',
    description: 'Set a center and drag to the radius',
    hint: 'Click the map to set the center, move to size the radius, then click again to set it. Esc cancels.',
  },
] as const;

export type DrawShape = (typeof DRAW_SHAPES)[number]['id'];

/**
 * What the Polygons section says while a shape is armed: how to draw it, and once
 * something is drawn, that overlapping shapes merge.
 *
 * @param {DrawShape} shape - The armed shape.
 * @param {number} drawnCount - Shapes drawn so far.
 * @return {string} The hint.
 */
export const drawingHint = (shape: DrawShape, drawnCount: number): string => {
  const hint = DRAW_SHAPES.find(({ id }) => id === shape)?.hint ?? '';
  return drawnCount > 0
    ? `${hint} Overlapping shapes merge into one area.`
    : hint;
};

/**
 * Boundary styles for the from-selected method: v8's two, and Epic 2's `tight`, a
 * concave hull the backend already accepts. Every host offers all three.
 */
export const BOUNDARY_STYLES = [
  {
    id: 'exact',
    title: 'Selected areas only',
    description:
      'Combine the exact geometry of each selected area into one composite (multi-part) area.',
  },
  {
    id: 'tight',
    title: 'Tight boundary',
    description:
      'Wrap the selection with a single boundary that follows its outer edges.',
  },
  {
    id: 'bounding',
    title: 'Bounding area',
    description:
      'Generate a single boundary that encloses all selected areas (convex hull of the selection).',
  },
] as const;

export type BoundaryStyle = (typeof BOUNDARY_STYLES)[number]['id'];

/** The styles a polygon selection offers, in the catalog and Map Query alike. */
export const CATALOG_BOUNDARY_STYLES: readonly BoundaryStyle[] = [
  'exact',
  'tight',
  'bounding',
];

/**
 * Selected areas reach the panel as areas; Map Query can also hand it wells,
 * which the backend receives by their row ids.
 */
export type SelectionKind = 'areas' | 'wells';

/**
 * Where the from-selected method's rows come from, when a host other than the
 * catalog supplies them. Point datasets can only be wrapped, so they get the
 * bounding hull and a buffer; polygon datasets get every style.
 */
export interface SelectionSource {
  kind: SelectionKind;
  /** The dataset's plural label ("Wells"), as the hint and the bounding style name it. */
  noun: string;
  /** Its singular, for a selection of one ("1 Well selected"). */
  singular: string;
  /** Where to check rows, said while none are. */
  emptyHint: string;
  geometry: 'polygon' | 'point';
  rows: SelectedArea[];
}

/** Default swatch the mock opens with. */
export const DEFAULT_DISPLAY_COLOR = '#FFCAB1';

/** The two verdicts the Verified control beside Display color can set. */
export type AreaVerification = 'correct' | 'incorrect';

export interface AreaDetails {
  name: string;
  displayColor: string;
  purpose: string;
  shared: boolean;
  notes: string;
  horizontalUnit: boolean;
  /**
   * The Verified control's choice, or null. It reaches the server as
   * `mark_correct`, which blocks automatic GIS updates to the new area from the
   * start, or `mark_incorrect`, which records the verdict without blocking them.
   */
  verification: AreaVerification | null;
}

export const EMPTY_DETAILS: AreaDetails = {
  name: '',
  displayColor: DEFAULT_DISPLAY_COLOR,
  purpose: '',
  shared: false,
  notes: '',
  horizontalUnit: true,
  verification: null,
};

/** A row checked in the catalog, as the from-selected method consumes it. */
export interface SelectedArea {
  id: string;
  name: string;
}
